// src/components/PlayOnline.tsx - Play Online with Custom Variants Support
import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Position } from "../types/Board.ts";
import { MoveValidator } from "../utils/moveValidator.ts";
import { MoveHistoryManager } from "../utils/MoveHistoryManager.ts";
import GamePanels, { MoveHistoryPanel } from "./GamePanels.tsx";
import { MultiplayerLobby } from "./multiplayer/MultiplayerLobby.tsx";
import { useMultiplayer } from "../hooks/useMultiplayer.ts";
import { useAuth } from "../context/AuthContext.tsx";
import "./PlayOnline.css";
import { AISettingsState } from "./AISettings.tsx";
import PageHeader from "./PageHeader.tsx";
import { TAB_DESCRIPTIONS } from "../constants/tabDescriptions.ts";
// Phase 1 & 2 Hooks
import { useGameState } from "../hooks/useGameState.ts";
import { useAIOpponent } from "../hooks/useAIOpponent.ts";
import { useGameClock } from "../hooks/useGameClock.ts";
import { useHistoryNavigation } from "../hooks/useHistoryNavigation.ts";
import { useMultiplayerGame } from "../hooks/useMultiplayerGame.ts";
// Phase 3 Hooks
import { useCustomVariants } from "../hooks/useCustomVariants.ts";
import { useBoardPresets } from "../hooks/useBoardPresets.ts";
import { useBoardColors } from "../hooks/useBoardColors.ts";
// Phase 3 Components
import ChessBoard from "./ChessBoard.tsx";
import BoardControls from "./BoardControls.tsx";
import PieceStatsPanel from "./PieceStatsPanel.tsx";
import GameModeSelector from "./GameModeSelector.tsx";
import AIStatsDisplay from "./AIStatsDisplay.tsx";
import GameStatistics, {
  GameStatisticsData,
  generateGameStatistics,
} from "./GameStatistics.tsx";
// Utils & Constants
import {
  calculatePieceStats,
  createBoardObject,
} from "../utils/gameHelpers.ts";
import { GAME_PRESETS } from "../constants/gamePresets.ts";
import { getSetupByName } from "../utils/boardSetup.ts";
// Game limits service
import { canPlayGame, incrementGameCount } from "../services/gameLimits.ts";

// API Base URL for game statistics
const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');

interface PlayOnlineProps {
  rows?: number;
  cols?: number;
  initialSetup?: string;
  aiSettings?: AISettingsState | null;
}

type GameMode = "local" | "vsAI" | "online";

const PlayOnline: React.FC<PlayOnlineProps> = ({
  rows: initialRows = 8,
  cols: initialCols = 8,
  initialSetup = "traditional",
  aiSettings,
}) => {
  // Get navigation state for multiplayer
  const location = useLocation();
  const navigationState = location.state as {
    roomId?: string;
    gameState?: any;
    boardSize?: number;
    variantId?: string;
    presetId?: string;
    whitePlayer?: string;
    blackPlayer?: string;
    isMultiplayer?: boolean;
    playerColor?: "white" | "black";
  } | null;

  // Board state
  const [boardRows, setBoardRows] = useState<number>(navigationState?.boardSize || initialRows);
  const [boardCols, setBoardCols] = useState<number>(navigationState?.boardSize || initialCols);
  const [fiftyMoveLimit, setFiftyMoveLimit] = useState<number>(50);
  const [seventyFiveMoveLimit, setSeventyFiveMoveLimit] = useState<number>(75);
  const [diagonalCaptureOnly, setDiagonalCaptureOnly] = useState<boolean>(false);
  const [showOpponentMoves, setShowOpponentMoves] = useState<boolean>(false);
  const [opponentHighlightedMoves, setOpponentHighlightedMoves] = useState<Position[]>([]);
  const [opponentSelectedSquare, setOpponentSelectedSquare] = useState<Position | null>(null);

  // Game mode - default to online if coming from multiplayer lobby
  const [gameMode, setGameMode] = useState<GameMode>(navigationState?.isMultiplayer ? "online" : "local");
  const [vsComputer, setVsComputer] = useState<boolean>(false);
  const [aiColor, setAiColor] = useState<"white" | "black">("black");

  // Multiplayer state
  const [showLobby, setShowLobby] = useState<boolean>(false);
  const [playerColor, setPlayerColor] = useState<"white" | "black" | null>(
    null
  );
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [isMyTurn, setIsMyTurn] = useState<boolean>(true);
  const [hasActiveOnlineGame, setHasActiveOnlineGame] =
    useState<boolean>(false);

  // Track the current game's variant name for statistics
  const [currentGameVariantName, setCurrentGameVariantName] = useState<string>("Traditional FIDE");

  // Resignation and statistics state
  const [showResignConfirmation, setShowResignConfirmation] =
    useState<boolean>(false);
  const [showGameStatistics, setShowGameStatistics] = useState<boolean>(false);
  const [gameStatistics, setGameStatistics] =
    useState<GameStatisticsData | null>(null);

  // Notification modal state
  const [notificationModal, setNotificationModal] = useState<{
    show: boolean;
    type: "success" | "error" | "info" | "warning";
    title: string;
    message: string;
  }>({ show: false, type: "info", title: "", message: "" });

  // Toast notification for quick messages (like copy confirmation)
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Managers
  const [moveValidator] = useState(() => new MoveValidator());
  const [historyManager] = useState(() => new MoveHistoryManager());

  // ⭐ Ref to prevent game reset during load
  const isLoadingGameRef = useRef<boolean>(false);

  // Initialize multiplayer state from navigation - needs joinRoom reference
  const navigationInitialized = useRef(false);

  // ⭐ NEW: Track initial board state for navigation
  const [initialBoardState, setInitialBoardState] = useState<Map<string, any>>(
    new Map()
  );

  // === CUSTOM HOOKS ===

  // Phase 3: Custom Variants
  const { customVariants } = useCustomVariants();

  // Debug: Log customVariants when they change
  useEffect(() => {
    console.log("🎮 PlayOnline: customVariants updated:", customVariants);
  }, [customVariants]);

  // Phase 3: Board Colors
  const { lightColor, darkColor, setLightColor, setDarkColor } =
    useBoardColors();

  // Phase 3: Board Presets
  const { currentPresetId, loadPreset, setCurrentPresetId } = useBoardPresets({
    standardPresets: GAME_PRESETS,
    customVariants,
    onBoardChange: (pieces, rows, cols, promotionPieces) => {
      setBoardRows(rows);
      setBoardCols(cols);
      setGamePieces(pieces);
      setPromotionPieces(promotionPieces);
      historyManager.setBoardSize(rows);
    },
  });

  // Phase 1: Game State
  const {
    gameState: {
      pieces,
      currentTurn,
      gameStatus,
      selectedSquare,
      highlightedMoves,
      capturedPieces,
      halfmoveClock,
      historyPosition,
      isReviewingHistory,
      moveHistory,
    },
    promotionState: {
      showPromotionDialog,
      promotionMove,
      availablePromotionPieces,
    },
    selectPiece,
    movePiece,
    handlePromotion,
    resetGame,
    setGamePieces,
    setPromotionPieces,
    setCurrentTurn,
    setGameStatus,
    setCapturedPieces,
    setMoveHistory,
    setShowPromotionDialog,
    setHistoryPosition,
    setIsReviewingHistory,
  } = useGameState({
    boardRows,
    boardCols,
    currentGameId: currentPresetId,
    moveValidator,
    historyManager,
    fiftyMoveLimit,
    seventyFiveMoveLimit,
    diagonalCaptureOnly,
    onMoveComplete: (piece, from, to, capturedPiece) => {
      if (gameMode === "online" && currentRoom) {
        const newPieces = new Map(pieces);
        const fromKey = `${from.row}-${from.col}`;
        newPieces.delete(fromKey);
        const toKey = `${to.row}-${to.col}`;
        const movedPiece = { ...piece, position: to, hasMoved: true };
        newPieces.set(toKey, movedPiece);

        console.log("📤 Sending move to server:");
        console.log(
          `   From: (${from.row},${from.col}) → To: (${to.row},${to.col})`
        );
        console.log(`   Piece: ${piece.type} (${piece.color})`);
        console.log(`   Captured: ${capturedPiece?.type || "none"}`);
        console.log(`   New pieces count: ${newPieces.size}`);

        sendMove(
          {
            piece: piece.type,
            from,
            to,
            captured: capturedPiece?.type,
            color: piece.color,
          },
          {
            pieces: Array.from(newPieces.entries()),
            currentTurn: currentTurn === "white" ? "black" : "white",
            moveHistory: historyManager.getMovesWithNumbers(),
            capturedPieces,
            gameStatus,
            boardSize: boardRows,
            preset: currentPresetId,
          }
        );
      }
      if (clockEnabled && !clockStarted) startClock();
    },
    onGameEnd: (result, winner) => {
      if (gameMode === "online" && currentRoom) {
        endGame(winner || "draw", result);
      }
    },
  });

  // Clock enabled state (separate from game mode)
  const [clockEnabledState, setClockEnabledState] = useState<boolean>(false);

  // Phase 2: Clock
  const {
    clockDuration,
    whiteTimeLeft,
    blackTimeLeft,
    clockRunning,
    clockStarted,
    showTimeoutModal,
    timeoutResult,
    showClockWarning,
    startClock,
    resetClock,
    handleClockDurationChange,
    toggleClock: toggleClockInternal,
    setShowTimeoutModal,
    setShowClockWarning,
  } = useGameClock({
    clockEnabled: clockEnabledState,
    currentTurn,
    isReviewingHistory,
    pieces,
    boardRows,
    boardCols,
    onTimeout: (winner, loser, result) => {
      setGameStatus(result === "draw" ? "stalemate" : "checkmate");
    },
  });

  // Clock is disabled in online mode, but can be toggled in local/AI modes
  const clockEnabled = gameMode === "online" ? false : clockEnabledState;

  // Wrapper for toggle that updates our local state
  const toggleClock = (enabled: boolean) => {
    if (gameMode !== "online") {
      setClockEnabledState(enabled);
      toggleClockInternal(enabled);
    }
  };

  // Phase 2: AI Opponent
  
  const { isAiThinking, aiThinkingTime, aiStats, engineType, resetAIStats } = useAIOpponent(
    {
      aiSettings,
      vsComputer,
      aiColor,
      currentTurn,
      gameStatus,
      isReviewingHistory,
      moveHistory,
      pieces,
      boardRows,
      boardCols,
      currentGameId: currentPresetId,
      onAIMove: (from, to, capturedPiece, aiContext) => movePiece(from, to, capturedPiece, aiContext),
    }
  );

  // Phase 2: History Navigation
  const { goBackward, goForward, goToCurrentPosition } = useHistoryNavigation({
    historyManager,
    currentGameId: currentPresetId,
    gamePresets: GAME_PRESETS,
    historyPosition,
    setHistoryPosition,
    setIsReviewingHistory,
    setGamePieces,
    setCapturedPieces,
    setCurrentTurn,
    initialPieces: initialBoardState, // ⭐ CRITICAL: Pass initial board state for navigation
  });

  // === MULTIPLAYER SETUP ===
  const { user } = useAuth();
  const SERVER_URL =
    process.env.REACT_APP_SERVER_URL || "";

  const {
    connected,
    currentRoom,
    opponentConnected,
    error: multiplayerError,
    opponentJoinedRoom,
    opponentUsername,
    chatMessages,
    receivedGameState,
    createRoom,
    joinRoom,
    makeMove: sendMove,
    endGame,
    deleteRoom,
    sendChatMessage,
    setOnOpponentMove,
    setOnGameStart,
    setOnGameFinished,
    setOnOpponentDisconnected,
    setOnOpponentJoinedRoom,
  } = useMultiplayer({
    serverUrl: SERVER_URL,
    userId: user?.userId,
    username: user?.username || "Guest",
    enabled: !!user?.userId && !!user?.username,
  });

  // Phase 2: Multiplayer Game
  useMultiplayerGame({
    gameMode,
    clockEnabled,
    clockDuration,
    historyManager,
    boardRows,
    receivedGameState,
    setOnOpponentMove,
    setOnGameStart,
    setOnGameFinished,
    setOnOpponentDisconnected,
    setOnOpponentJoinedRoom,
    onOpponentMove: (piecesMap, turn) => {
      setGamePieces(piecesMap);
      setCurrentTurn(turn);

      // Check if the current player (whose turn it now is) is in check
      const isInCheck = moveValidator.isKingInCheck(piecesMap, turn, boardRows, boardCols);
      if (isInCheck) {
        console.log(`♚ ${turn} is in CHECK after opponent's move!`);
        setGameStatus("check");
      } else {
        setGameStatus("active");
      }
    },
    onGameStart: () => resetGame(true), // Keep pieces - they were already loaded by loadPreset
    onGameEnd: () => {},
    setShowLobby,
    setHasActiveOnlineGame,
    setBoardRows,
    setBoardCols,
    setGamePieces,
    setCurrentGameId: setCurrentPresetId,
    setCapturedPieces,
    setMoveHistory,
    resetClock,
    gamePresets: GAME_PRESETS,
  });

  // Room state management
  useEffect(() => {
    if (currentRoom) {
      setRoomCode(currentRoom.roomId);
      setPlayerColor(currentRoom.color);
      if (opponentConnected) {
        setShowLobby(false);
        setHasActiveOnlineGame(true);
      }
    } else {
      setHasActiveOnlineGame(false);
    }
  }, [currentRoom, opponentConnected]);

  // Extract variant name from received game state (for guests joining)
  useEffect(() => {
    if (receivedGameState?.gameName) {
      console.log("🎮 Setting variant name from received game state:", receivedGameState.gameName);
      setCurrentGameVariantName(receivedGameState.gameName);
    }
  }, [receivedGameState]);

  // Initialize multiplayer from navigation state (after joinRoom is available)
  useEffect(() => {
    if (navigationInitialized.current) return;
    if (!navigationState?.isMultiplayer || !navigationState?.roomId) return;
    if (!connected) return; // Wait for socket connection

    console.log("🎮 Initializing multiplayer game from navigation state");
    console.log("   Room ID:", navigationState.roomId);
    console.log("   Board Size:", navigationState.boardSize);
    console.log("   Variant ID:", navigationState.variantId);
    console.log("   White Player:", navigationState.whitePlayer);
    console.log("   Black Player:", navigationState.blackPlayer);
    console.log("   My Color:", navigationState.playerColor);

    navigationInitialized.current = true;

    // Set room code and player color
    setRoomCode(navigationState.roomId);
    if (navigationState.playerColor) {
      setPlayerColor(navigationState.playerColor);
    }

    // Set board size if provided
    if (navigationState.boardSize) {
      setBoardRows(navigationState.boardSize);
      setBoardCols(navigationState.boardSize);
    }

    // Load the board preset to initialize pieces
    // Use presetId from navigation or derive from board size (e.g., 8 -> "0808-001")
    const boardSize = navigationState.boardSize || 8;
    const presetId = navigationState.presetId ||
      `${boardSize.toString().padStart(2, '0')}${boardSize.toString().padStart(2, '0')}-001`;

    console.log("🎮 Loading preset for multiplayer:", presetId);
    console.log("   Navigation presetId:", navigationState.presetId);
    console.log("   Navigation boardSize:", navigationState.boardSize);
    console.log("   Navigation variantId:", navigationState.variantId);

    // Set variant name from navigation state (variantId now contains the name, e.g., "Perfect Chess")
    if (navigationState.variantId) {
      setCurrentGameVariantName(navigationState.variantId);
    } else {
      // Derive from preset
      const preset = GAME_PRESETS.find(p => p.id === presetId);
      if (preset) {
        setCurrentGameVariantName(preset.name);
      }
    }

    loadPreset(presetId);
    resetGame(true);

    // Set hasActiveOnlineGame AFTER loading the preset
    setHasActiveOnlineGame(true);

    // Join the socket room to receive moves
    console.log("🔌 Joining socket room:", navigationState.roomId);
    joinRoom(navigationState.roomId);

  }, [navigationState, connected, joinRoom, loadPreset, resetGame]);

  useEffect(() => {
    if (gameMode === "online" && playerColor) {
      setIsMyTurn(currentTurn === playerColor);
    } else {
      setIsMyTurn(true);
    }
  }, [currentTurn, playerColor, gameMode]);

  // Board initialization on preset change
  useEffect(() => {
    // ⭐ Guard: Don't reset if we're loading a saved game
    if (isLoadingGameRef.current) {
      console.log("⏭️ Skipping board reset - loading saved game");
      return;
    }

    if (gameMode === "online" && (hasActiveOnlineGame || currentRoom)) {
      console.log("⏭️ Skipping board reset - online game in progress");
      return;
    }

    console.log("🎮 Loading preset:", currentPresetId);
    loadPreset(currentPresetId);
    resetGame(true);
    resetClock();
    resetAIStats();

    // ⭐ NEW: Track initial board state for navigation
    setTimeout(() => {
      setInitialBoardState(new Map(pieces));
      console.log(
        "💾 Initial board state tracked for new game:",
        pieces.size,
        "pieces"
      );
    }, 100);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPresetId, hasActiveOnlineGame, gameMode, currentRoom]);

  // === EVENT HANDLERS ===

  const handleModeChange = async (mode: GameMode) => {
    // Check AI game limit before switching to AI mode
    if (mode === "vsAI" && user?.userId) {
      const limitStatus = await canPlayGame(user.userId, 'ai');
      if (!limitStatus.canPlay) {
        setNotificationModal({
          show: true,
          type: "warning",
          title: "AI Game Limit Reached",
          message: `${limitStatus.message}\n\nYou've used ${limitStatus.currentCount} of ${limitStatus.limit} AI games this month (${limitStatus.membershipLevel} membership).\n\nUpgrade your privacy preferences in your profile to increase your game limit.`
        });
        return; // Don't switch to AI mode
      }

      // Increment AI game count when starting
      const incrementResult = await incrementGameCount(user.userId, 'ai');
      if (incrementResult.success) {
        console.log(`AI game started. ${incrementResult.remaining} games remaining this month.`);
      }
    }

    setGameMode(mode);
    setVsComputer(mode === "vsAI");
    setShowLobby(false);
  };

  const handleResignClick = () => {
    setShowResignConfirmation(true);
  };

  const handleResignConfirm = async () => {
    // Ensure we have a valid player color
    const myColor = playerColor || "white";
    const opponentColor = myColor === "white" ? "black" : "white";

    // Get user IDs - the winner is the opponent, the loser is the current user
    const winnerUserId = currentRoom?.opponentUserId || opponentUsername || "Unknown";
    const loserUserId = user?.userId || user?.username || "Unknown";

    // Fetch actual player ratings from the database
    let winnerRating = 1000; // Default rating
    let loserRating = 1000;

    try {
      // Fetch winner's rating
      if (winnerUserId && winnerUserId !== "Unknown") {
        const winnerResponse = await fetch(`${API_BASE_URL}/api/game-statistics/user/${winnerUserId}`);
        if (winnerResponse.ok) {
          const winnerData = await winnerResponse.json();
          if (winnerData.success && winnerData.statistics?.current_rating) {
            winnerRating = winnerData.statistics.current_rating;
            console.log(`📊 Winner rating fetched: ${winnerRating}`);
          }
        }
      }

      // Fetch loser's rating (current user)
      if (loserUserId && loserUserId !== "Unknown") {
        const loserResponse = await fetch(`${API_BASE_URL}/api/game-statistics/user/${loserUserId}`);
        if (loserResponse.ok) {
          const loserData = await loserResponse.json();
          if (loserData.success && loserData.statistics?.current_rating) {
            loserRating = loserData.statistics.current_rating;
            console.log(`📊 Loser rating fetched: ${loserRating}`);
          }
        }
      }
    } catch (error) {
      console.warn("⚠️ Could not fetch player ratings, using defaults:", error);
    }

    // Get the actual move count from historyManager which tracks both players' moves
    const actualMoveCount = historyManager.getMovesWithNumbers().length;
    console.log(`📊 Move count - moveHistory state: ${moveHistory.length}, historyManager: ${actualMoveCount}`);

    const stats = generateGameStatistics({
      gameName: currentGameVariantName,
      boardSize: boardRows,
      gameResult: `${opponentColor.charAt(0).toUpperCase() + opponentColor.slice(1)} wins - ${myColor.charAt(0).toUpperCase() + myColor.slice(1)} resigned`,
      winningPlayerId: winnerUserId,
      losingPlayerId: loserUserId,
      winningPlayerColor: opponentColor,
      losingPlayerColor: myColor,
      winningPlayerRanking: winnerRating,
      losingPlayerRanking: loserRating,
      numberOfMoves: actualMoveCount > 0 ? actualMoveCount : moveHistory.length,
      capturedPieces: capturedPieces,
      timeLimit: clockDuration,
      roomNumber: roomCode || "Unknown",
    });

    console.log("📊 Generated game statistics:", stats);
    console.log("   Winner User ID:", winnerUserId);
    console.log("   Loser User ID:", loserUserId);
    console.log("   Winner Rating:", winnerRating);
    console.log("   Loser Rating:", loserRating);

    setGameStatistics(stats);
    setShowResignConfirmation(false);

    if (currentRoom) {
      endGame(opponentColor, "resignation");
    }

    setShowGameStatistics(true);
  };

  const handleResignCancel = () => {
    setShowResignConfirmation(false);
  };

  const handleCloseStatistics = () => {
    setShowGameStatistics(false);
    setGameStatistics(null);
    setHasActiveOnlineGame(false);
    setShowLobby(false);
  };

  /**
   * Load saved game WITH NAVIGATION SUPPORT
   * - Loads both initial and final board states
   * - Enables move-by-move navigation
   */
  const handleLoadMoveHistory = (savedGame: any) => {
    console.log(
      "=== handleLoadMoveHistory called (WITH NAVIGATION SUPPORT) ==="
    );
    console.log("Saved game:", {
      name: savedGame.name,
      id: savedGame.id,
      boardSize: savedGame.board_size,
      totalMoves: savedGame.total_moves,
      hasPieces: !!savedGame.pieces,
      hasInitialPieces: !!savedGame.initial_pieces,
      piecesCount: savedGame.pieces?.length || 0,
      initialPiecesCount: savedGame.initial_pieces?.length || 0,
      movesCount: savedGame.moves?.length || 0,
    });

    if (!savedGame) {
      console.error("No saved game provided");
      setNotificationModal({
        show: true,
        type: "error",
        title: "Error",
        message: "No saved game data",
      });
      return;
    }

    // ⭐ CRITICAL: Prevent useEffect from resetting the game during load
    isLoadingGameRef.current = true;
    console.log("🔒 Load lock enabled - preventing game reset");

    try {
      // PRIMARY METHOD: Load from saved board state (FAST)
      if (savedGame.pieces && savedGame.pieces.length > 0) {
        console.log("✅ Loading from saved board state (fast path)...");

        // Step 1: Restore board configuration
        console.log("Setting board size:", savedGame.board_size);
        setBoardRows(savedGame.board_size);
        setBoardCols(savedGame.board_size);
        setCurrentPresetId(savedGame.game_preset);

        // Step 2: ⭐ RESTORE INITIAL BOARD STATE (for navigation)
        let initialPiecesMap: Map<string, any>;

        if (savedGame.initial_pieces && savedGame.initial_pieces.length > 0) {
          // We have saved initial state - use it!
          initialPiecesMap = new Map<string, any>(savedGame.initial_pieces);
          console.log(
            "✅ Restored INITIAL board state:",
            initialPiecesMap.size,
            "pieces"
          );
        } else {
          // Fallback: Use preset's initial setup
          console.log("⚠️ No initial state saved, using preset setup");
          const preset =
            GAME_PRESETS.find((p) => p.id === savedGame.game_preset) ||
            GAME_PRESETS[0];
          initialPiecesMap = getSetupByName(preset.setup, savedGame.board_size);
          console.log(
            "📋 Loaded preset initial state:",
            initialPiecesMap.size,
            "pieces"
          );
        }

        // ⭐ CRITICAL: Store initial state for navigation
        setInitialBoardState(new Map(initialPiecesMap));
        console.log("💾 Initial board state stored for navigation");

        // Step 3: Convert final pieces array back to Map
        const finalPiecesMap = new Map<string, any>(savedGame.pieces);
        console.log(
          "Restored FINAL board state:",
          finalPiecesMap.size,
          "pieces"
        );

        // Step 4: Restore game state
        setCurrentTurn(savedGame.current_turn || "white");
        setGameStatus(savedGame.game_status || "active");
        setCapturedPieces(savedGame.captured_pieces || []);

        console.log("Game state restored:", {
          currentTurn: savedGame.current_turn,
          gameStatus: savedGame.game_status,
          capturedCount: savedGame.captured_pieces?.length || 0,
        });

        // Step 5: ⭐ DIRECTLY RESTORE MOVES
        const moves = savedGame.moves || [];
        console.log("📜 Directly restoring", moves.length, "moves...");

        historyManager.clear();
        historyManager.setBoardSize(savedGame.board_size);

        if (moves.length > 0) {
          (historyManager as any).moves = moves.map((move: any) => ({
            id: move.id,
            pieceType: move.pieceType,
            pieceColor: move.pieceColor,
            from: move.from,
            to: move.to,
            capturedPiece: move.capturedPiece,
            notation: move.notation,
            isCheck: move.isCheck || false,
            isCheckmate: move.isCheckmate || false,
            isEnPassant: move.isEnPassant || false,
            enPassantCaptureSquare: move.enPassantCaptureSquare,
            promotedTo: move.promotedTo,
            timestamp: move.timestamp ? new Date(move.timestamp) : new Date(),
          }));

          console.log("✅ Moves restored directly to history manager");
        }

        // Step 6: ⭐ GET FORMATTED MOVES
        console.log(
          "📜 Getting formatted moves with numbers from history manager..."
        );
        const formattedMoves = historyManager.getMovesWithNumbers();
        console.log("📜 Formatted moves count:", formattedMoves.length);

        setMoveHistory([...formattedMoves]);

        // Step 7: ⭐ START AT INITIAL POSITION FOR NAVIGATION
        console.log("📍 Setting to INITIAL board position (before moves)");

        // ⭐ CRITICAL FIX: Batch all state updates to prevent race conditions
        // Use setTimeout to ensure all states update together
        setTimeout(() => {
          setGamePieces(new Map(initialPiecesMap)); // Show initial position
          setHistoryPosition(-1);
          setIsReviewingHistory(moves.length > 0);
          setCapturedPieces([]); // Empty array, never undefined
          setCurrentTurn("white");
          setGameStatus("active");

          console.log("✅ Initial state set:", {
            pieces: initialPiecesMap.size,
            position: -1,
            capturedPieces: 0,
            turn: "white",
            status: "active",
          });
        }, 0);

        // Step 8: Reset clock if enabled
        if (clockEnabled) {
          console.log("⏰ Resetting clock");
          resetClock();
        }

        // Step 9: Reset AI stats if in AI mode
        if (vsComputer) {
          console.log("🤖 Resetting AI stats");
          resetAIStats();
        }

        // Step 10: Success!
        console.log("✅ Game loaded successfully!");
        console.log(
          "   📋 Board:",
          `${savedGame.board_size}×${savedGame.board_size}`
        );
        console.log("   🎬 Initial pieces:", initialPiecesMap.size);
        console.log("   ♟️  Final pieces:", finalPiecesMap.size);
        console.log("   📜 Moves:", formattedMoves.length);
        console.log("   📍 Position:", -1, "(at start, ready to navigate)");

        // ⭐ CRITICAL: Re-enable game reset after load completes
        setTimeout(() => {
          isLoadingGameRef.current = false;
          console.log("🔓 Load lock released - game reset re-enabled");
        }, 100);

        setNotificationModal({
          show: true,
          type: "success",
          title: `Loaded "${savedGame.name}"`,
          message: `${savedGame.total_moves} moves restored\n${savedGame.board_size}×${savedGame.board_size} board\n\nClick the forward button to step through moves!`,
        });
      } else {
        console.log("⚠️ No board state found in saved game");

        setNotificationModal({
          show: true,
          type: "warning",
          title: "Old Save Format Detected",
          message: "This saved game doesn't include complete board state.\n\nPlease re-save your games to use the new fast-loading feature.",
        });
      }
    } catch (error: any) {
      console.error("=== Error loading move history ===");
      console.error("Error:", error);
      console.error("Stack:", error.stack);

      isLoadingGameRef.current = false;
      console.log("🔓 Load lock released (error) - game reset re-enabled");

      setNotificationModal({
        show: true,
        type: "error",
        title: "Error Loading Game",
        message: `${error.message}\n\nPlease try loading a different save.`,
      });
    }
  };

  const handleSaveToDatabase = async (stats: GameStatisticsData) => {
    console.log("💾 Saving game statistics to database:", stats);

    // Get move history from historyManager which tracks both players' moves
    const historyMoves = historyManager.getMovesWithNumbers();
    const movesToSave = historyMoves.length > 0 ? historyMoves : moveHistory;

    console.log(`📊 Moves to save: historyManager=${historyMoves.length}, state=${moveHistory.length}, using=${movesToSave.length}`);

    try {
      const response = await fetch(`${API_BASE_URL}/api/game-statistics/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...stats,
          // Use historyManager moves which track both players
          moveHistory: movesToSave,
          capturedPieces: capturedPieces,
          initialPosition: currentPresetId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save game statistics');
      }

      const result = await response.json();
      console.log("✅ Statistics saved successfully:", result);

      if (result.rankingChanges) {
        console.log("📊 Ranking changes:", result.rankingChanges);
      }
    } catch (error) {
      console.error("❌ Error saving game statistics:", error);
      throw error;
    }
  };

  const handleSquareClick = (row: number, col: number) => {
    if (gameMode === "online" && !isMyTurn) return;
    if (vsComputer && currentTurn === aiColor) return;

    const clickedPos = { row, col };
    const board = createBoardObject(
      pieces,
      boardRows,
      boardCols,
      currentPresetId
    );
    const clickedPiece = pieces.get(`${row}-${col}`);

    // Track if clicking the same opponent piece (for toggle behavior)
    const wasOpponentPieceSelected = opponentSelectedSquare !== null;
    const clickingSameOpponentPiece = wasOpponentPieceSelected &&
      opponentSelectedSquare.row === row &&
      opponentSelectedSquare.col === col;

    // Clear opponent highlighted moves when clicking
    if (opponentHighlightedMoves.length > 0) {
      setOpponentHighlightedMoves([]);
      setOpponentSelectedSquare(null);
    }

    // Priority 1: If a piece is selected, handle move/capture first
    if (selectedSquare) {
      const selectedPiece = pieces.get(
        `${selectedSquare.row}-${selectedSquare.col}`
      );
      if (selectedSquare.row === row && selectedSquare.col === col) {
        // Clicking on the same square - deselect
        if (selectedPiece) selectPiece(selectedSquare, selectedPiece);
        return;
      } else if (selectedPiece) {
        const validation = moveValidator.validateMove(
          selectedPiece,
          selectedSquare,
          clickedPos,
          board,
          diagonalCaptureOnly
        );
        if (validation.isValid) {
          // Valid move - execute it
          if (clockEnabled && !clockStarted && moveHistory.length === 0) {
            setShowClockWarning(true);
            return;
          }
          movePiece(selectedSquare, clickedPos, validation.capturedPiece);
          return;
        } else {
          // Invalid move - check if clicking own piece to reselect
          if (clickedPiece && clickedPiece.color === currentTurn) {
            selectPiece(clickedPos, clickedPiece);
            return;
          }
          // Invalid move and clicked on opponent piece - show their moves if enabled
          if (showOpponentMoves && clickedPiece && clickedPiece.color !== currentTurn) {
            // Check if clicking the same opponent piece (toggle off)
            if (clickingSameOpponentPiece) {
              // Already cleared above, just return
              return;
            }
            const opponentMoves = moveValidator.getLegalMoves(clickedPiece, board, diagonalCaptureOnly);
            setOpponentHighlightedMoves(opponentMoves);
            setOpponentSelectedSquare(clickedPos);
            return;
          }
        }
      }
    }

    // Priority 2: Select own piece
    if (clickedPiece && clickedPiece.color === currentTurn) {
      selectPiece(clickedPos, clickedPiece);
      return;
    }

    // Priority 3: Show opponent moves when no piece selected
    if (showOpponentMoves && clickedPiece && clickedPiece.color !== currentTurn) {
      // Check if clicking the same opponent piece (toggle off)
      if (clickingSameOpponentPiece) {
        // Already cleared above, just return
        return;
      }
      const opponentMoves = moveValidator.getLegalMoves(clickedPiece, board, diagonalCaptureOnly);
      setOpponentHighlightedMoves(opponentMoves);
      setOpponentSelectedSquare(clickedPos);
    }
  };

  const { whiteCount, blackCount, whiteValue, blackValue } =
    calculatePieceStats(pieces, boardRows);

  // === RENDER ===

  return (
    <div className="play-online-wrapper" style={{ paddingTop: "10px" }}>
      <PageHeader
        icon={TAB_DESCRIPTIONS.play.icon}
        title={TAB_DESCRIPTIONS.play.label}
        description={TAB_DESCRIPTIONS.play.description}
      />

      <div style={{ marginTop: "5px" }}>
        {gameMode === "online" && !user ? (
          <div
            style={{
              padding: "10px",
              textAlign: "center",
              backgroundColor: "#ffffff",
              border: "2px solid #ddd",
              borderRadius: "8px",
              margin: "5px",
              maxWidth: "600px",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <h2 style={{ color: "#333", marginBottom: "5px" }}>
              ⚠️ Authentication Required
            </h2>
            <p
              style={{ fontSize: "16px", marginBottom: "5px", color: "#333" }}
            >
              You must be logged in to play online multiplayer games.
            </p>
            <div
              style={{ display: "flex", gap: "5px", justifyContent: "center" }}
            >
              <button
                onClick={() => setGameMode("local")}
                style={{
                  padding: "5px 10px",
                  backgroundColor: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Switch to Local Mode
              </button>
              <button
                onClick={() => (window.location.href = "/login")}
                style={{
                  padding: "5px 10px",
                  backgroundColor: "#2196F3",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Go to Login
              </button>
            </div>
          </div>
        ) : gameMode === "online" && showLobby ? (
          <div className="multiplayer-lobby-container">
            <MultiplayerLobby
              serverUrl={SERVER_URL}
              connected={connected}
              error={multiplayerError}
              currentUsername={user?.username}
              currentUserId={user?.userId}
              roomCode={roomCode}
              opponentJoinedRoom={opponentJoinedRoom}
              opponentUsername={opponentUsername}
              onCreateGame={(boardSize, variantId, gameName) => {
                const preset =
                  GAME_PRESETS.find((p) => p.rows === boardSize) ||
                  GAME_PRESETS[0];
                const piecesMap = getSetupByName(preset.setup, preset.rows);
                setBoardRows(preset.rows);
                setBoardCols(preset.rows);
                setGamePieces(piecesMap);
                setCurrentPresetId(preset.id);
                // Track variant name for game statistics - variantId now contains the name (e.g., "Perfect Chess")
                const variantName = variantId || preset.name;
                setCurrentGameVariantName(variantName);
                createRoom(
                  preset.id,
                  preset.rows,
                  {
                    pieces: Array.from(piecesMap.entries()),
                    boardSize: preset.rows,
                    preset: preset.setup,
                    gameName: variantName,
                  },
                  variantName
                );
              }}
              onJoinGame={(roomId) => joinRoom(roomId)}
              onDeleteGame={(roomId) => deleteRoom(roomId)}
              onBack={() => setShowLobby(false)}
            />
            {roomCode && !opponentConnected && (
              <div className="waiting-room">
                <h3>Waiting for opponent...</h3>
                <div className="room-code-display">
                  <span>
                    Room Code: <strong>{roomCode}</strong>
                  </span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(roomCode);
                      setToastMessage("Room code copied!");
                      setTimeout(() => setToastMessage(null), 5000);
                    }}
                    className="copy-btn"
                  >
                    Copy Code
                  </button>
                </div>
                <p>Share this code with your opponent</p>
              </div>
            )}
          </div>
        ) : (
          <div className="game-content-wrapper">
            {vsComputer && (
              <AIStatsDisplay
               isThinking={isAiThinking}
               thinkingTime={aiThinkingTime}
               stats={aiStats}
               engineType={engineType}
               difficulty={aiSettings?.difficulty}
               maxThinkingTime={aiSettings?.maxThinkingTime}
               threadCount={aiSettings?.threadCount}
               maxDepth={aiSettings?.depthCap}
              />
            )}

            <div className="chess-game-layout">
              <div
                className="left-panel-column"
                style={{ width: "300px", minWidth: "300px" }}
              >
                {gameMode === "online" && (
                  <button
                    onClick={handleResignClick}
                    style={{
                      width: "100%",
                      padding: "12px 15px",
                      backgroundColor: "#d32f2f",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      transition: "all 0.2s",
                      whiteSpace: "nowrap",
                      marginBottom: "5px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#b71c1c";
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 8px rgba(0,0,0,0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#d32f2f";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 4px rgba(0,0,0,0.2)";
                    }}
                  >
                    🏳️ Resign from Online Game
                  </button>
                )}

                <GameModeSelector
                  gameMode={gameMode}
                  connected={connected}
                  roomCode={roomCode}
                  opponentUsername={opponentUsername}
                  onModeChange={handleModeChange}
                  onShowLobby={() => setShowLobby(true)}
                />

                <MoveHistoryPanel
                  moveHistory={moveHistory}
                  gameStatus={gameStatus}
                  currentTurn={currentTurn}
                  historyPosition={historyPosition}
                  isReviewingHistory={isReviewingHistory}
                  halfmoveClock={halfmoveClock}
                  fiftyMoveLimit={fiftyMoveLimit}
                  seventyFiveMoveLimit={seventyFiveMoveLimit}
                  boardRows={boardRows}
                  currentGameId={currentPresetId}
                  onLoadMoveHistory={handleLoadMoveHistory}
                  goBackward={goBackward}
                  goForward={goForward}
                  goToCurrentPosition={goToCurrentPosition}
                  claim50MoveDraw={() => {
                    if (halfmoveClock >= fiftyMoveLimit * 2) {
                      setGameStatus("stalemate");
                      if (gameMode === "online" && currentRoom)
                        endGame("draw", "stalemate");
                      setNotificationModal({
                        show: true,
                        type: "info",
                        title: "Draw Claimed",
                        message: "Draw claimed by 50-move rule!",
                      });
                    }
                  }}
                  pieces={pieces}
                  capturedPieces={capturedPieces}
                  initialPieces={initialBoardState} // ⭐ NEW PROP
                />
              </div>

              <div className="board-and-right-panels">
                <ChessBoard
                  pieces={pieces}
                  boardRows={boardRows}
                  boardCols={boardCols}
                  selectedSquare={selectedSquare}
                  highlightedMoves={highlightedMoves}
                  opponentHighlightedMoves={opponentHighlightedMoves}
                  lightSquareColor={lightColor}
                  darkSquareColor={darkColor}
                  onSquareClick={handleSquareClick}
                  isReviewingHistory={isReviewingHistory}
                  gameStatus={gameStatus}
                  isAiThinking={isAiThinking}
                  disabled={!isMyTurn && gameMode === "online"}
                />

                <div className="right-panels-container">
                  {/* Select Game Panel - top aligned with board */}
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      borderRadius: "6px",
                      border: "2px solid #333",
                      padding: "8px",
                      marginBottom: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        <label
                          style={{
                            fontSize: "13px",
                            fontWeight: "bold",
                            color: "#333",
                          }}
                        >
                          Select Game:
                        </label>
                        <select
                          value={currentPresetId}
                          onChange={(e) => setCurrentPresetId(e.target.value)}
                          disabled={gameMode === "online"}
                          style={{
                            flex: 1,
                            minWidth: "220px",
                            width: "100%",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            border: "2px solid #4CAF50",
                            fontSize: "14px",
                            fontWeight: "bold",
                            backgroundColor:
                              gameMode === "online" ? "#f0f0f0" : "white",
                            cursor:
                              gameMode === "online" ? "not-allowed" : "pointer",
                          }}
                        >
                          <optgroup label="Standard Games">
                            {GAME_PRESETS.map((preset) => (
                              <option key={preset.id} value={preset.id}>
                                {preset.name}
                              </option>
                            ))}
                          </optgroup>
                          {customVariants && customVariants.length > 0 && (
                            <optgroup label="My Variants">
                              {customVariants.map((variant) => (
                                <option key={variant.id} value={variant.id}>
                                  {variant.name} ({variant.boardSize}×{variant.boardSize})
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                      </div>

                      <button
                        onClick={() => setCurrentPresetId("empty")}
                        disabled={gameMode === "online"}
                        style={{
                          width: "100%",
                          padding: "6px 12px",
                          backgroundColor:
                            gameMode === "online" ? "#ccc" : "#f44336",
                          color: "white",
                          border: "none",
                          borderRadius: "3px",
                          cursor: gameMode === "online" ? "not-allowed" : "pointer",
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                      >
                        Clear Board
                      </button>

                      {gameMode === "online" && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "4px 6px",
                            backgroundColor: "#ffffff",
                            border: "1px solid #ddd",
                            borderRadius: "3px",
                            fontSize: "11px",
                          }}
                        >
                          <span style={{ fontSize: "12px" }}>⚠️</span>
                          <span style={{ fontWeight: "bold", color: "#333" }}>
                            Board configuration locked during online play!
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <GamePanels
                    boardRows={boardRows}
                    currentGameId={currentPresetId}
                    availablePromotionPieces={availablePromotionPieces}
                    fiftyMoveLimit={fiftyMoveLimit}
                    seventyFiveMoveLimit={seventyFiveMoveLimit}
                    setFiftyMoveLimit={setFiftyMoveLimit}
                    setSeventyFiveMoveLimit={setSeventyFiveMoveLimit}
                    diagonalCaptureOnly={diagonalCaptureOnly}
                    setDiagonalCaptureOnly={setDiagonalCaptureOnly}
                    showOpponentMoves={showOpponentMoves}
                    setShowOpponentMoves={setShowOpponentMoves}
                    clockEnabled={clockEnabled}
                    clockDuration={clockDuration}
                    whiteTimeLeft={whiteTimeLeft}
                    blackTimeLeft={blackTimeLeft}
                    clockRunning={clockRunning}
                    clockStarted={clockStarted}
                    currentTurn={currentTurn}
                    toggleClock={toggleClock}
                    handleClockDurationChange={handleClockDurationChange}
                    startClock={startClock}
                    resetClock={resetClock}
                    capturedPieces={capturedPieces}
                    materialAdvantage={historyManager.getMaterialAdvantage()}
                    capturedValues={historyManager.getCapturedPiecesValue()}
                    moveHistory={moveHistory}
                    gameStatus={gameStatus}
                    historyPosition={historyPosition}
                    isReviewingHistory={isReviewingHistory}
                    halfmoveClock={halfmoveClock}
                    goBackward={goBackward}
                    goForward={goForward}
                    goToCurrentPosition={goToCurrentPosition}
                    claim50MoveDraw={() => {}}
                    show50MoveWarning={false}
                    setShow50MoveWarning={() => {}}
                    showClockWarning={showClockWarning}
                    setShowClockWarning={setShowClockWarning}
                    showTimeoutModal={showTimeoutModal}
                    setShowTimeoutModal={setShowTimeoutModal}
                    timeoutResult={timeoutResult}
                    showPromotionDialog={showPromotionDialog}
                    setShowPromotionDialog={setShowPromotionDialog}
                    promotionMove={promotionMove}
                    getPieceAt={(row, col) => pieces.get(`${row}-${col}`)}
                    handlePromotion={handlePromotion}
                    pieces={pieces}
                    gameMode={gameMode}
                    chatMessages={chatMessages}
                    onSendChatMessage={sendChatMessage}
                    currentUsername={user?.username}
                    opponentUsername={opponentUsername}
                    chatConnected={connected && !!currentRoom}
                  />
                </div>
              </div>
            </div>

            {/* Move Rule Counter Panel - above color selection */}
            <PieceStatsPanel
              halfmoveClock={halfmoveClock}
              fiftyMoveLimit={fiftyMoveLimit}
              seventyFiveMoveLimit={seventyFiveMoveLimit}
              totalPieces={pieces.size}
              whiteCount={whiteCount}
              blackCount={blackCount}
              whiteValue={whiteValue}
              blackValue={blackValue}
              selectedSquare={selectedSquare}
              boardRows={boardRows}
              pieces={pieces}
            />

            {/* Color Selection Panel - below move rule counter */}
            <BoardControls
              lightSquareColor={lightColor}
              darkSquareColor={darkColor}
              onLightColorChange={setLightColor}
              onDarkColorChange={setDarkColor}
              vsComputer={vsComputer}
              aiColor={aiColor}
              aiSettings={aiSettings}
              onAiColorChange={setAiColor}
              isAiThinking={isAiThinking}
              moveHistoryLength={moveHistory.length}
              gameMode={gameMode}
            />
          </div>
        )}
      </div>

      {showResignConfirmation && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
          onClick={handleResignCancel}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "12px",
              maxWidth: "450px",
              width: "90%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              style={{
                margin: "0 0 20px 0",
                color: "#d32f2f",
                fontSize: "24px",
              }}
            >
              🏳️ Confirm Resignation
            </h2>
            <p
              style={{
                marginBottom: "25px",
                fontSize: "16px",
                lineHeight: "1.5",
              }}
            >
              Are you sure you want to resign from this game? This action cannot
              be undone and will count as a loss.
            </p>
            <div
              style={{
                display: "flex",
                gap: "15px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleResignCancel}
                style={{
                  padding: "10px 25px",
                  backgroundColor: "#757575",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleResignConfirm}
                style={{
                  padding: "10px 25px",
                  backgroundColor: "#d32f2f",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "600",
                }}
              >
                Yes, Resign
              </button>
            </div>
          </div>
        </div>
      )}

      <GameStatistics
        isOpen={showGameStatistics}
        statistics={gameStatistics}
        onClose={handleCloseStatistics}
        onSaveToDatabase={handleSaveToDatabase}
      />

      {/* Notification Modal */}
      {notificationModal.show && (
        <div
          className="modal-overlay"
          onClick={() =>
            setNotificationModal({
              show: false,
              type: "info",
              title: "",
              message: "",
            })
          }
        >
          <div
            className="modal-content notification-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              borderTop: `4px solid ${
                notificationModal.type === "success"
                  ? "#4CAF50"
                  : notificationModal.type === "error"
                  ? "#f44336"
                  : notificationModal.type === "warning"
                  ? "#ff9800"
                  : "#2196F3"
              }`,
            }}
          >
            <div className="modal-header">
              <h2
                style={{
                  color:
                    notificationModal.type === "success"
                      ? "#4CAF50"
                      : notificationModal.type === "error"
                      ? "#f44336"
                      : notificationModal.type === "warning"
                      ? "#ff9800"
                      : "#2196F3",
                }}
              >
                {notificationModal.title}
              </h2>
            </div>
            <div className="modal-body">
              <p style={{ whiteSpace: "pre-line" }}>{notificationModal.message}</p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() =>
                  setNotificationModal({
                    show: false,
                    type: "info",
                    title: "",
                    message: "",
                  })
                }
                className="modal-btn confirm-btn"
                style={{
                  backgroundColor:
                    notificationModal.type === "success"
                      ? "#4CAF50"
                      : notificationModal.type === "error"
                      ? "#f44336"
                      : notificationModal.type === "warning"
                      ? "#ff9800"
                      : "#2196F3",
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div
          style={{
            position: "fixed",
            bottom: "30px",
            right: "30px",
            backgroundColor: "#333",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            zIndex: 10001,
            animation: "fadeIn 0.3s",
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default PlayOnline;
